import { Clock } from 'lucide-react'
import { useState } from 'react'
import { useCreateWpToken } from '@/api/hooks/use-wordpress'
import { CopyButton } from '@/components/ui/copy-button'

const TOKEN_EXPIRY_MINUTES = 60

interface Props {
  onTokenGenerated?: () => void
}

export function GenerateTokenInline({ onTokenGenerated }: Props): React.JSX.Element {
  const createToken = useCreateWpToken()
  const [createdToken, setCreatedToken] = useState<string | null>(null)
  const [name, setName] = useState('')

  function handleGenerate(): void {
    const tokenName = name.trim() || 'WordPress Site'
    createToken.mutate(
      { name: tokenName, ttl: '1h' },
      {
        onSuccess: (res) => {
          setCreatedToken(res.token)
          onTokenGenerated?.()
        },
      },
    )
  }

  function handleReset(): void {
    setCreatedToken(null)
    setName('')
  }

  if (createdToken) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg border border-warning/30 bg-amber-50 p-3">
          <p className="text-sm font-medium text-amber-800">
            Copy this token now — it will not be shown again.
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-surface-1 p-3">
          <code className="flex-1 break-all font-mono text-sm select-all">{createdToken}</code>
          <CopyButton value={createdToken} />
        </div>
        <div className="flex items-center gap-2 text-xs text-text-tertiary">
          <Clock className="h-3.5 w-3.5" />
          <span>Paste this in your WordPress plugin settings.</span>
        </div>
        <button
          type="button"
          onClick={handleReset}
          className="text-sm text-brand-500 hover:text-brand-600"
        >
          Generate another token
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div>
        <label
          htmlFor="wp-site-name-inline"
          className="block text-sm font-medium text-text-secondary"
        >
          Site name
        </label>
        <input
          id="wp-site-name-inline"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Company Blog"
          className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary focus:border-brand-500 focus:outline-none"
        />
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-text-tertiary">
          <Clock className="h-3.5 w-3.5" />
          <span>Token expires in {TOKEN_EXPIRY_MINUTES} minutes</span>
        </div>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={createToken.isPending}
          className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
        >
          {createToken.isPending ? 'Generating...' : 'Generate Token'}
        </button>
      </div>
    </div>
  )
}
